import FormDate from 'components/form/FormDate';
import FormPhoneNumber from 'components/form/FormPhoneNumber';
import FormSelect from 'components/form/FormSelect';
import FormText from 'components/form/FormText';
import FormTextArea from 'components/form/FormTextArea';
import { DivConst } from 'constants/divConst';
import { AppHelper } from 'helpers/appHelper';
import { FieldErrors, UseFormRegister } from 'react-hook-form';
import { RespondentFormStateType } from 'stores/atoms/respondentFormState';
import { RespondentFieldType } from 'types/respondentFieldType';

/**
 * 回答者情報入力画面
 */
export default function RespondentForm(props: {
  respondentField: RespondentFieldType;
  reportMailDescribeHtml: string;
  lineFlag: boolean;
  index: number;
  register: UseFormRegister<RespondentFormStateType>;
  errors: FieldErrors<RespondentFormStateType>;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  const _renderForm = () => {
    switch (props.respondentField.respondentFieldDiv) {
      case DivConst.items.respondentFieldDiv.standard.value:
        return _renderStandardFieldForm();
      case DivConst.items.respondentFieldDiv.custom.value:
        return _renderCustomFieldForm();
      default:
        return null;
    }
  };

  const _renderStandardFieldForm = () => {
    switch (props.respondentField.respondentStandardFieldDiv) {
      case DivConst.items.respondentStandardFieldDiv.mailAddress.value:
        return _renderEmailForm();
      case DivConst.items.respondentStandardFieldDiv.name.value:
        return _renderNameForm();
      case DivConst.items.respondentStandardFieldDiv.gender.value:
        return _renderGenderForm();
      case DivConst.items.respondentStandardFieldDiv.birthDate.value:
        return _renderBirthDateForm();
      case DivConst.items.respondentStandardFieldDiv.phoneNumber.value:
        return _renderPhoneNumberForm();
      case DivConst.items.respondentStandardFieldDiv.residence.value:
        return _renderResidenceForm();
      default:
        return null;
    }
  };

  const _renderCustomFieldForm = () => {
    switch (props.respondentField.respondentCustomFieldInputDiv) {
      case DivConst.items.respondentCustomFieldInputDiv.text.value:
        return _renderCustomTextForm();
      case DivConst.items.respondentCustomFieldInputDiv.numeric.value:
        return _renderCustomNumericForm();
      default:
        return null;
    }
  };

  /**
   * メールアドレス入力フォーム
   */
  const _renderEmailForm = () => {
    return (
      <>
        {_renderDescription('あなたのメールアドレスを教えてください')}
        <div className="form-group">
          <FormText
            key={props.respondentField.id}
            name="mailAddress"
            format="email"
            placeholder="Email"
            required={true}
            maxLength={300}
            register={props.register}
            errors={props.errors}
          />
        </div>
        {!props.lineFlag && (
          <p className="note text-center quiz-mail-note">
            {props.reportMailDescribeHtml?.split('\n').map(line => (
              <>
                {line}
                <br />
              </>
            ))}
          </p>
        )}
      </>
    );
  };

  /**
   * 名前入力フォーム
   */
  const _renderNameForm = () => {
    return (
      <>
        {_renderDescription('あなたのお名前を教えてください')}
        <FormText
          key={props.respondentField.id}
          name="name"
          maxLength={100}
          required={true}
          register={props.register}
          errors={props.errors}
        />
      </>
    );
  };

  /**
   * 性別入力フォーム
   */
  const _renderGenderForm = () => {
    return (
      <>
        {_renderDescription('あなたの性別を教えてください')}
        <FormSelect
          key={props.respondentField.id}
          name="gender"
          options={AppHelper.getDivSelectOptions('gender')}
          blank={true}
          blankLabel="選択してください"
          required={true}
          register={props.register}
          errors={props.errors}
        />
      </>
    );
  };

  /**
   * 生年月日入力フォーム
   */
  const _renderBirthDateForm = () => {
    return (
      <>
        {_renderDescription('あなたの生年月日を教えてください')}
        <FormDate
          key={props.respondentField.id}
          name="birthDate"
          placeholder="1990/01/01"
          required={true}
          register={props.register}
          errors={props.errors}
          min={new Date(1800, 0, 1)}
          max={new Date()}
        />
      </>
    );
  };

  /**
   * 電話番号入力フォーム
   */
  const _renderPhoneNumberForm = () => {
    return (
      <>
        {_renderDescription('あなたの電話番号を教えてください')}
        <FormPhoneNumber
          key={props.respondentField.id}
          name="phoneNumber"
          placeholder="12345678900"
          required={true}
          register={props.register}
          errors={props.errors}
        />
      </>
    );
  };

  /**
   * 居住エリア入力フォーム
   */
  const _renderResidenceForm = () => {
    return (
      <>
        {_renderDescription('あなたの居住エリアを教えてください')}
        <FormSelect
          key={props.respondentField.id}
          name="residence"
          options={AppHelper.getDivSelectOptions('prefecture')}
          blank={true}
          blankLabel="選択してください"
          required={true}
          register={props.register}
          errors={props.errors}
        />
      </>
    );
  };

  /**
   * 自由項目: テキスト入力フォーム
   */
  const _renderCustomTextForm = () => {
    return (
      <>
        {_renderDescription(`${props.respondentField.label}`)}
        {props.respondentField.maxLength ===
        DivConst.items.respondentCustomFieldMaxLength.maxLength20.value ? (
          <FormText
            key={props.respondentField.id}
            name={AppHelper.getDivLabel(
              'respondentCustomFieldNameDiv',
              props.respondentField.respondentCustomFieldDiv
            )}
            placeholder={props.respondentField.placeholder}
            required={true}
            register={props.register}
            errors={props.errors}
            maxLength={props.respondentField.maxLength}
          />
        ) : (
          <FormTextArea
            key={props.respondentField.id}
            name={AppHelper.getDivLabel(
              'respondentCustomFieldNameDiv',
              props.respondentField.respondentCustomFieldDiv
            )}
            placeholder={props.respondentField.placeholder}
            required={true}
            register={props.register}
            errors={props.errors}
            maxLength={props.respondentField.maxLength}
          />
        )}
      </>
    );
  };

  /**
   * 自由項目: 数値入力フォーム
   */
  const _renderCustomNumericForm = () => {
    return (
      <>
        {_renderDescription(`${props.respondentField.label}`)}
        <FormText
          key={props.respondentField.id}
          name={AppHelper.getDivLabel(
            'respondentCustomFieldNameDiv',
            props.respondentField.respondentCustomFieldDiv
          )}
          placeholder={props.respondentField.placeholder}
          format="numeric"
          required={true}
          register={props.register}
          errors={props.errors}
          maxLength={props.respondentField.maxLength}
        />
      </>
    );
  };

  const _renderDescription = (description: string) => {
    return (
      <div className="d-flex d-flex-quiz">
        <div className="flex-shrink-0 flex-left">Q{props.index}</div>
        <div className="flex-grow-1 flex-right">{description}</div>
      </div>
    );
  };

  return <>{_renderForm()}</>;
}
